import { type CSSProperties, type FormEvent, useId, useState } from "react";
import { Link } from "react-router";

import { supabase } from "../lib/supabase.ts";
import { PATHS } from "../routes/routes.constants.ts";
import { rememberIntendedRole } from "./intendedRole.ts";
import type { Role } from "./roles.ts";

type Props = {
  role: Role;
  open: boolean;
  onToggle: () => void;
};

/**
 * One collapsible sign-in card. Exactly one card is open at a time — the
 * parent owns that choice, this component owns the credentials.
 *
 * All five cards call the same sign-in. The role is NOT an auth parameter:
 * there is one user directory, and which workspaces an account can open is
 * derived server-side from its grants. The clicked role is remembered only as
 * a hint for where to land afterwards.
 */
export function RoleSignIn({ role, open, onToggle }: Props) {
  const id = useId();
  const panelId = `${id}-panel`;
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setPending(true);
    setError(null);

    // Consumed once by the router to pick a landing workspace, then cleared.
    // It must never travel to the server or become authorization state.
    rememberIntendedRole(role.key);

    const { error: signInError } = await supabase.auth.signInWithPassword({
      email,
      password,
    });

    if (signInError) {
      setPending(false);
      setError(signInError.message);
      return;
    }
    // On success the session listener re-renders the app away from this form,
    // so `pending` is deliberately left set — clearing it would flash the
    // button back to its idle label first.
  }

  return (
    <div
      className={`role${open ? " role--open" : ""}`}
      style={{ "--role": role.accent } as CSSProperties}
    >
      <button
        type="button"
        className={`role__toggle${open ? " role__toggle--open" : ""}`}
        aria-expanded={open}
        aria-controls={panelId}
        onClick={onToggle}
      >
        <span>{role.label}</span>
        <span className="role__chevron" aria-hidden="true">
          ›
        </span>
      </button>

      {open && (
        <form id={panelId} className="role__form" onSubmit={(event) => void onSubmit(event)}>
          <div className="role__field">
            <label className="role__label" htmlFor={`${id}-email`}>
              Email address
            </label>
            <input
              id={`${id}-email`}
              className="role__input"
              type="email"
              autoComplete="email"
              required
              disabled={pending}
              placeholder="you@example.com"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
            />
          </div>

          <div className="role__field">
            <label className="role__label" htmlFor={`${id}-password`}>
              Password
            </label>
            <input
              id={`${id}-password`}
              className="role__input"
              type="password"
              autoComplete="current-password"
              required
              disabled={pending}
              placeholder="••••••••"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
            />
          </div>

          {/*
            A LINK NOW, AND IT WAS A DEAD BUTTON UNTIL 5 OCTOBER.

            `type="button"` with no handler — styled, present, and doing nothing. For as
            long as it sat here, `signup.emails.ts` was telling people whose address
            already had an account to *"use the reset link on the sign-in page"*. Both
            sentences are true as of OND-19.

            A `Link` rather than a button that navigates, because it is an address
            somebody may want to open in a new tab or send to themselves.
          */}
          <Link className="role__forgot" to={PATHS.forgotPassword}>
            Forgot password?
          </Link>

          {error && (
            <p className="role__error" role="alert">
              {error}
            </p>
          )}

          <button type="submit" className="role__submit" disabled={pending}>
            {pending ? "Signing in…" : "Sign in →"}
          </button>
        </form>
      )}
    </div>
  );
}
